<template>
    <div class="commission-detail-wrapper">
        <div :class="$style.container" v-if="commission">
            <div :class="$style.detailHeader">
                <div :class="$style.detailHeaderTitle">
                    <h1>{{ commission.name }}</h1>
                </div>
                <div :class="$style.detailButtons">
                    <button v-if="commission.link" :class="$style.linkBtn" @click="openExternal">링크이동</button>
                    <!-- <button :class="$style.backBtn" @click="goBack">돌아가기</button> -->
                    <button class="back-btn" @click="$router.push('/commission')">목록으로</button>
                </div>
            </div>

            <div :class="$style.detailContent">
                <div>
                    <div :class="$style.detailPosterContainer">
                        <img v-if="commission.thumbnailUrl" :src="commission.thumbnailUrl" :alt="commission.name"
                            :class="$style.detailPoster" />
                        <div v-else :class="$style.noImagePlaceholder">
                            <span>썸네일 없음</span>
                        </div>
                    </div>
                    <!-- 문의하기 버튼 (모바일에서 하단 고정) -->
                    <div :class="$style.contactBtn">
                        <button :class="$style.contactBtnInner" @click="toggleCommentSection">
                            문의하기
                        </button>
                    </div>
                </div>

                <div :class="$style.detailInfo">
                    <div :class="$style.infoTable">
                        <div :class="$style.infoRow" v-if="commission.category">
                            <div :class="$style.infoLabel">카테고리</div>
                            <div :class="$style.infoValue">
                                <span :class="[$style.categoryBadge, $style[`cat-${commission.category}`]]">
                                    {{ categoryLabel(commission.category) }}
                                </span>
                            </div>
                        </div>
                        <div :class="$style.infoRow" v-if="commission.description">
                            <div :class="$style.infoLabel">설명</div>
                            <div :class="[$style.infoValue, $style.commissionDescription]">
                                {{ commission.description }}
                            </div>
                        </div>
                        <!-- 관리자용 작성자 정보 -->
                        <div :class="$style.infoRow"
                            v-if="userStore.auth === 'admin' && (commission.createdBy || commission.createdByNickname)">
                            <div :class="$style.infoLabel">작성자</div>
                            <div :class="$style.infoValue">
                                <div :class="$style.creatorInfo">
                                    <span :class="$style.creatorNickname">{{
                                        commission.createdByNickname || "알 수 없음"
                                    }}</span>
                                    <span :class="$style.creatorEmail">({{ commission.createdBy }})</span>
                                </div>
                            </div>
                        </div>
                        <!-- 수정 버튼 (권한 있는 경우만) -->
                        <div :class="$style.infoRow" v-if="canEdit">
                            <div :class="$style.infoLabel">관리</div>
                            <div :class="$style.infoValue">
                                <button :class="$style.editBtn" @click="openEditForm">
                                    수정하기
                                </button>
                            </div>
                        </div>
                        <div :class="$style.infoRow" v-if="commission.createdAt">
                            <div :class="$style.infoLabel">등록일</div>
                            <div :class="$style.infoValue">
                                {{ formatDate(commission.createdAt) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <LoadingScreen loading-text="커미션 정보를 불러오는 중..." />
        </div>

        <!-- 커미션 수정 모달 -->
        <CommissionForm :is-visible="showEditForm" mode="update" :commission-data="commissionEditData"
            @close-popup="closeEditForm" />

        <!-- 댓글 섹션 오버레이 -->
        <div v-if="showCommentSection" :class="$style.commentOverlay" @click="toggleCommentSection"></div>

        <!-- 댓글 섹션 (하단에서 올라오는 패널) -->
        <div :class="[$style.commentPanel, { [$style.commentPanelOpen]: showCommentSection }]">
            <div :class="$style.commentPanelHeader">
                <h3 :class="$style.commentPanelTitle">문의하기</h3>
                <button :class="$style.commentPanelClose" @click="toggleCommentSection">×</button>
            </div>
            <div :class="$style.commentPanelContent">
                <CommentSection v-if="commission?.id" :board-id="commission.id" :collection-type="'commissions'"
                    :post-writer-email="commission?.createdBy || ''" />
            </div>
        </div>
    </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import CommissionForm from "@/components/modal/form/CommissionForm.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import CommentSection from "@/components/CommentSection.vue";
import { setPageMeta } from "@/utils/pageMeta";

export default {
    name: "CommissionDetail",
    components: {
        CommissionForm,
        LoadingScreen,
        CommentSection,
    },
    data() {
        return {
            loading: true,
            commission: null,
            showEditForm: false,
            showCommentSection: false,
        };
    },
    computed: {
        userStore() {
            return useUserStore();
        },
        canEdit() {
            if (!this.commission) return false;
            // 관리자이거나 본인이 작성한 커미션인 경우
            return this.userStore.auth === "admin" ||
                (this.userStore.isLogin && this.commission.createdBy === this.userStore.email);
        },
        commissionEditData() {
            if (!this.commission) return null;
            return {
                id: this.commission.id,
                name: this.commission.name,
                category: this.commission.category,
                description: this.commission.description,
                link: this.commission.link,
                thumbnail: this.commission.thumbnail, // 원본 파일명
                thumbnailUrl: this.commission.thumbnailUrl, // 표시용 URL
            };
        },
    },
    async created() {
        await this.fetchCommission();
    },
    methods: {
        async fetchCommission() {
            try {
                this.loading = true;
                const id = this.$route.params.id;
                if (!id) return;

                const snap = await getDoc(doc(firestore, "commissions", id));
                if (!snap.exists()) {
                    this.commission = null;
                    return;
                }
                const data = snap.data();
                let thumbnailUrl = data.thumbnail || "";
                const originalThumbnail = data.thumbnail || ""; // 원본 파일명 저장

                if (thumbnailUrl && !thumbnailUrl.startsWith("http")) {
                    try {
                        const imageRef = ref(storage, `commission/${thumbnailUrl}`);
                        thumbnailUrl = await getDownloadURL(imageRef);
                    } catch {
                        thumbnailUrl = "";
                    }
                }

                this.commission = {
                    id,
                    category: data.category || "",
                    name: data.name || "",
                    link: data.link || "",
                    description: data.description || "",
                    thumbnail: originalThumbnail, // 원본 파일명
                    thumbnailUrl, // 표시용 URL
                    createdBy: data.createdBy || "",
                    createdByNickname: data.createdByNickname || "",
                    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
                };
                setPageMeta({
                    title: this.commission.name,
                    description: this.commission.description || undefined,
                    image: thumbnailUrl || undefined,
                });
            } catch (e) {
                this.commission = null;
            } finally {
                this.loading = false;
            }
        },
        openEditForm() {
            this.showEditForm = true;
        },
        closeEditForm() {
            this.showEditForm = false;
            // 모달이 닫힌 후 데이터 새로고침
            this.fetchCommission();
        },
        openExternal() {
            if (this.commission?.link) {
                window.open(this.commission.link, "_blank");
            }
        },
        toggleCommentSection() {
            this.showCommentSection = !this.showCommentSection;
        },
        goBack() {
            this.$router.back();
        },
        categoryLabel(value) {
            const map = {
                mixing: "믹싱",
                illustration: "일러스트",
                design: "디자인",
                video: "영상",
            };
            return map[value] || "기타";
        },
        formatDate(date) {
            if (!(date instanceof Date)) return "";
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}.${m}.${d}`;
        },
    },
};
</script>

<style scoped>
.commission-detail-wrapper {
    width: 100%;
}
</style>

<style module>
.container {
    width: 95%;
    margin: auto;
    background: #fff;
    border-radius: 16px;
}

.detailHeader {
    display: flex;
    gap: 0px;
    justify-content: space-between;
    align-items: center;
}

.detailHeaderTitle {
    width: 50%;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detailHeaderTitle h1 {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    font-family: "NanumSquareRoundB", sans-serif;
}

.detailButtons {
    display: flex;
    justify-content: end;
    gap: 16px;
}

.linkBtn {
    background: #222;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 15px;
    font-size: 18px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s ease;
}

.linkBtn:hover {
    background: #000;
}

.backBtn {
    background: #fff;
    color: #222;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 10px 15px;
    font-size: 18px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.backBtn:hover {
    background: #f5f5f5;
    border-color: #999;
}

.detailContent {
    display: flex;
    flex-direction: row;
    gap: 32px;
    margin-top: 10px;
    align-items: flex-start;
    justify-content: center;
}

.detailPosterContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 10px;
}

.detailPoster {
    height: 350px;
    width: 350px;
    object-fit: cover;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.noImagePlaceholder {
    height: 350px;
    width: 350px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    color: #888;
    font-size: 14px;
}

.detailInfo {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    margin-top: 40px;
    gap: 16px;
}

.infoTable {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 260px;
}

.infoRow {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    margin-bottom: 2px;
}

.infoLabel {
    width: 120px;
    color: #b0b0b0;
    font-size: 16px;
    font-weight: 400;
    text-align: left;
    font-family: "NanumSquareRoundR", sans-serif;
}

.infoValue {
    color: #222;
    font-size: 18px;
    font-weight: 500;
    text-align: left;
    word-break: break-all;
    font-family: "NanumSquareRoundR", sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.categoryBadge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
}

.cat-mixing {
    background: #8e44ad;
}

.cat-illustration {
    background: #e67e22;
}

.cat-design {
    background: #16a085;
}

.cat-video {
    background: #2980b9;
}

.commissionDescription {
    font-style: italic;
    color: #666;
    font-weight: 400;
    font-size: 16px;
}

.editBtn {
    background: #4a9eff;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: "NanumSquareRoundR", sans-serif;
    transition: background 0.2s ease;
}

.editBtn:hover {
    background: #2196f3;
}

.creatorInfo {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.creatorNickname {
    font-weight: bold;
    color: #333;
    font-size: 16px;
}

.creatorEmail {
    color: #666;
    font-size: 14px;
}

/* 문의하기 버튼 스타일 */
.contactBtn {
    display: block;
    width: 100%;
    margin-top: 16px;
}

.contactBtnInner {
    display: block;
    width: 100%;
    margin: auto;
    background: #fff;
    color: #2196f3;
    border: 2px solid #2196f3;
    border-radius: 20px;
    padding: 10px 0;
    font-size: 20px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
    cursor: pointer;
    transition: all 0.3s ease;
    letter-spacing: 1px;
    font-family: "NanumSquareRoundB", sans-serif;
}

.contactBtnInner:hover {
    background: #f0f8ff;
    border-color: #1976d2;
    color: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.contactBtnInner:active {
    transform: translateY(0);
    background: #e3f2fd;
    box-shadow: 0 2px 6px rgba(33, 150, 243, 0.15);
}

/* 댓글 오버레이 */
.commentOverlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 199;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

/* 댓글 패널 스타일 */
.commentPanel {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 80vh;
    background: white;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    z-index: 200;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.commentPanelOpen {
    transform: translateY(0);
}

.commentPanelHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e9ecef;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
}

.commentPanelTitle {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    font-family: "NanumSquareRoundB", sans-serif;
}

.commentPanelClose {
    background: none;
    border: none;
    font-size: 32px;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    line-height: 1;
}

.commentPanelClose:hover {
    background: #f5f5f5;
    color: #333;
}

.commentPanelContent {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

/* ========================================
   Medium (481px ~ 1024px)
   ======================================== */
@media (max-width: 1024px) {
    .container {
        width: 98%;
        padding: 15px;
        border-radius: 12px;
    }

    .detailHeader {
        gap: 12px;
    }

    .detailHeaderTitle {
        width: 60%;
    }

    .detailHeaderTitle h1 {
        font-size: 20px;
    }

    .detailButtons {
        gap: 10px;
    }

    .linkBtn,
    .backBtn {
        font-size: 15px;
        padding: 8px 12px;
        border-radius: 6px;
    }

    .detailContent {
        gap: 24px;
        margin-top: 15px;
    }

    .detailPosterContainer {
        gap: 12px;
    }

    .detailPoster {
        height: 300px;
        width: 300px;
        border-radius: 10px;
    }

    .noImagePlaceholder {
        height: 300px;
        width: 300px;
        border-radius: 10px;
    }

    .detailInfo {
        margin-top: 20px;
        gap: 12px;
    }

    .infoTable {
        gap: 10px;
        min-width: 220px;
    }

    .infoRow {
        gap: 16px;
    }

    .infoLabel {
        width: 100px;
        font-size: 14px;
    }

    .infoValue {
        font-size: 16px;
    }

    .categoryBadge {
        padding: 3px 10px;
        font-size: 11px;
    }

    .commissionDescription {
        font-size: 14px;
    }

    .editBtn {
        font-size: 13px;
        padding: 5px 10px;
    }

    .creatorNickname {
        font-size: 14px;
    }

    .creatorEmail {
        font-size: 12px;
    }

    .contactBtn {
        margin-top: 12px;
    }

    .contactBtnInner {
        padding: 8px 0;
        font-size: 18px;
        border-radius: 16px;
    }

    .commentPanel {
        max-height: 75vh;
    }

    .commentPanelHeader {
        padding: 16px;
    }

    .commentPanelTitle {
        font-size: 18px;
    }

    .commentPanelClose {
        font-size: 28px;
        width: 36px;
        height: 36px;
    }
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
    .container {
        width: 100%;
        padding: 10px;
        border-radius: 8px;
        padding-bottom: 100px;
    }

    .detailHeader {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }

    .detailHeaderTitle {
        width: 100%;
    }

    .detailHeaderTitle h1 {
        font-size: 16px;
    }

    .detailButtons {
        width: 100%;
        gap: 8px;
        justify-content: flex-start;
    }

    .linkBtn,
    .backBtn {
        font-size: 12px;
        padding: 6px 10px;
        border-radius: 6px;
    }

    .detailContent {
        flex-direction: column;
        gap: 15px;
        margin-top: 10px;
        align-items: stretch;
    }

    .detailPosterContainer {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
    }

    .detailPoster {
        max-width: 100%;
        width: auto;
        height: auto;
        max-height: 280px;
        border-radius: 8px;
    }

    .noImagePlaceholder {
        width: 100%;
        max-width: 280px;
        height: 250px;
        border-radius: 8px;
        font-size: 12px;
    }

    .detailInfo {
        gap: 8px;
        min-width: 0;
        width: 100%;
        padding: 0;
        margin-top: 0;
    }

    .infoTable {
        min-width: 0;
        width: 100%;
        gap: 6px;
    }

    .infoRow {
        gap: 12px;
    }

    .infoLabel {
        width: 55px;
        min-width: 55px;
        font-size: 11px;
    }

    .infoValue {
        font-size: 12px;
    }

    .categoryBadge {
        padding: 3px 8px;
        font-size: 10px;
    }

    .commissionDescription {
        font-size: 11px;
    }

    .editBtn {
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 4px;
    }

    .creatorInfo {
        gap: 2px;
    }

    .creatorNickname {
        font-size: 12px;
    }

    .creatorEmail {
        font-size: 10px;
    }

    /* 모바일에서 문의하기 버튼을 하단에 고정 */
    .contactBtn {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        z-index: 100;
        border-radius: 0;
        padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
        margin: 0;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
        transition: all 0.2s ease;
        background: #fff;
        border-top: 1px solid #e0e0e0;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    }

    .contactBtn:active,
    .contactBtn:focus {
        background: #f5f5f5;
        transform: none;
    }

    .contactBtn:focus {
        outline: none;
    }

    .contactBtn:not(:active):not(:focus) {
        background: #fff;
        transform: none;
    }

    .contactBtnInner {
        width: 100%;
        background: white;
        color: #2196f3;
        border: 2px solid #2196f3;
        border-radius: 30px;
        font-size: 15px;
        font-weight: bold;
        padding: 12px 0;
        cursor: pointer;
        transition: all 0.2s ease;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
        font-family: "NanumSquareRoundB", sans-serif;
    }

    .contactBtnInner:active {
        background: #e3f2fd;
        color: #1976d2;
        border-color: #1976d2;
        transform: scale(0.98);
    }

    .contactBtnInner:focus {
        outline: none;
    }

    .commentPanel {
        max-height: 85vh;
        border-radius: 16px 16px 0 0;
    }

    .commentPanelHeader {
        padding: 12px 14px;
    }

    .commentPanelTitle {
        font-size: 16px;
    }

    .commentPanelClose {
        font-size: 24px;
        width: 32px;
        height: 32px;
    }
}
</style>
